import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// Sistema de notificação usando Material-UI

import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { CreditProvider } from './contexts/CreditContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Layout from './components/Layout';
import AdminLayout from './components/admin/AdminLayout';
import { useBackendWarmup } from './hooks/useBackendWarmup';
import { queryClient } from './lib/queryClient';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

import ChatPage from './pages/chat/ChatPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import NewDocumentPage from './pages/documents/NewDocumentPage';
import DocumentEditorPage from './pages/documents/DocumentEditorPage';
import NewDocumentEditorPage from './pages/documents/NewDocumentEditorPage';
import AIDocumentGeneratorPage from './pages/documents/AIDocumentGeneratorPage';
import DocumentFieldsPage from './pages/documents/DocumentFieldsPage';
import SearchPage from './pages/search/SearchPage';
import StatisticsPage from './pages/statistics/StatisticsPage';
import ProfilePage from './pages/profile/ProfilePage';
import DashboardPage from './pages/dashboard/DashboardPage';
import UsagePage from './pages/usage/UsagePage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import BetaDashboardPage from './pages/admin/BetaDashboardPage';
import StatusPage from './pages/StatusPage';
import NotFoundPage from './pages/NotFoundPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiesPage from './pages/CookiesPage';
import KeyboardShortcutsPage from './pages/KeyboardShortcutsPage';
import HelpPage from './pages/HelpPage';
import JurisprudencePage from './pages/jurisprudence/JurisprudencePage';
import JurisprudenceSearchPage from './pages/jurisprudence/JurisprudenceSearchPage';
import ProcessAlertsPage from './pages/alerts/ProcessAlertsPage';
import DocumentWizard from './components/documents/DocumentWizard';
import DocumentImprovementPage from './pages/documents/DocumentImprovementPage';
import FlowSelector from './components/documents/FlowSelector';
const ConfigurableWizard = React.lazy(() => import('./components/documents/ConfigurableWizard'));
import DraftsList from './components/documents/DraftsList';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import NotificationManagementPage from './pages/admin/NotificationManagementPage';
import EmailCampaignPage from './pages/admin/EmailCampaignPage';
import TemplateImportPage from './pages/admin/TemplateImportPage';
import CreditManagementPage from './pages/admin/CreditManagementPage';
import PaymentSettingsPage from './pages/admin/PaymentSettingsPage';
import JurisprudenceImportPage from './pages/admin/JurisprudenceImportPage';
import JurisprudenceListPage from './pages/admin/JurisprudenceListPage';
import AISettingsPage from './pages/admin/AISettingsPage';
import DocumentFlowsPage from './pages/admin/DocumentFlowsPage';
import DocumentFlowEditorPage from './pages/admin/DocumentFlowEditorPage';

// Pandora Admin Pages
import PandoraDashboardPage from './pages/admin/pandora/PandoraDashboardPage';
import PandoraUploadPage from './pages/admin/pandora/PandoraUploadPage';
import PandoraDocumentsPage from './pages/admin/pandora/PandoraDocumentsPage';
import PandoraDocumentDetailPage from './pages/admin/pandora/PandoraDocumentDetailPage';
import PandoraValidationPage from './pages/admin/pandora/PandoraValidationPage';
import PandoraTaxonomyPage from './pages/admin/pandora/PandoraTaxonomyPage';
import PandoraValidatedContentPage from './pages/admin/pandora/PandoraValidatedContentPage';

// Global styles
const globalStyles = `
  html, body, #root {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    max-width: none;
    overflow-x: hidden;
  }
  
  * {
    box-sizing: border-box;
  }
`;

// Componente interno para usar o hook de warmup
// Título da aba por rota (o prefixo mais específico vence)
const PAGE_TITLES: [string, string][] = [
  ['/dashboard', 'Início'],
  ['/chat', 'Chat Jurídico'],
  ['/documents/editor', 'Editor'],
  ['/documents/flows', 'Criar Documento'],
  ['/documents/flow', 'Criar Documento'],
  ['/documents/drafts', 'Rascunhos'],
  ['/documents', 'Meus Documentos'],
  ['/jurisprudence', 'Jurisprudência'],
  ['/search', 'Consulta Processual'],
  ['/alertas', 'Alertas de Processos'],
  ['/usage', 'Meu Consumo'],
  ['/checkout', 'Pagamento'],
  ['/profile', 'Meu Perfil'],
  ['/statistics', 'Estatísticas'],
  ['/help', 'Central de Ajuda'],
  ['/status', 'Status'],
  ['/login', 'Entrar'],
  ['/register', 'Cadastro'],
  ['/reset-password', 'Recuperar Senha'],
  ['/terms', 'Termos de Uso'],
  ['/privacy', 'Política de Privacidade'],
  ['/cookies', 'Política de Cookies'],
  ['/admin', 'Admin'],
];

const usePageTitle = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = PAGE_TITLES.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/') || pathname.startsWith(prefix + '-'));
    document.title = match ? `${match[1]} · Advogada Parceira` : 'Advogada Parceira';
  }, [pathname]);
};

const AppRoutes: React.FC = () => {
  // Mantém o backend aquecido
  useBackendWarmup();
  usePageTitle();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/keyboard-shortcuts" element={<KeyboardShortcutsPage />} />
      
      {/* Help with Layout */}
      <Route element={<Layout />}>
        <Route path="/help" element={<HelpPage />} />
      </Route>
      <Route path="/status" element={<StatusPage />} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:sessionId" element={<ChatPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/new" element={<Navigate to="/documents/flows" replace />} />
          <Route path="/documents/wizard" element={<Navigate to="/documents/flows" replace />} />
          <Route path="/documents/improve" element={<DocumentImprovementPage />} />
          <Route path="/documents/ai-generator" element={<Navigate to="/documents/flows" replace />} />
          <Route path="/documents/fields/:documentId" element={<DocumentFieldsPage />} />
          <Route path="/documents/editor/:documentId" element={<DocumentEditorPage />} />
          <Route path="/documents/editor-advanced/:documentId" element={<NewDocumentEditorPage />} />
          <Route path="/documents/:id/edit" element={<DocumentEditorPage />} />
          <Route path="/documents/flows" element={<FlowSelector />} />
          <Route path="/documents/flow/:flowSlug" element={<Suspense fallback={null}><ConfigurableWizard /></Suspense>} />
          <Route path="/documents/drafts" element={<Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}><Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>Rascunhos Pendentes</Typography><DraftsList /></Box>} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/jurisprudence" element={<JurisprudencePage />} />
          <Route path="/jurisprudence/search" element={<JurisprudenceSearchPage />} />
          <Route path="/alertas" element={<ProcessAlertsPage />} />
          <Route path="/statistics" element={<StatisticsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          
          {/* Admin Routes - flow_admin + full admin (Fluxos, Pandora, Dashboard) */}
          <Route element={<AdminRoute allowFlowAdmin />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/document-flows" element={<DocumentFlowsPage />} />
              <Route path="/admin/document-flows/:id" element={<DocumentFlowEditorPage />} />
              <Route path="/admin/pandora" element={<PandoraDashboardPage />} />
              <Route path="/admin/pandora/upload" element={<PandoraUploadPage />} />
              <Route path="/admin/pandora/documents" element={<PandoraDocumentsPage />} />
              <Route path="/admin/pandora/documents/:id" element={<PandoraDocumentDetailPage />} />
              <Route path="/admin/pandora/validation" element={<PandoraValidationPage />} />
              <Route path="/admin/pandora/taxonomy" element={<PandoraTaxonomyPage />} />
              <Route path="/admin/pandora/validated" element={<PandoraValidatedContentPage />} />
              {/* Full admin only routes */}
              <Route path="/admin/users" element={<UserManagementPage />} />
              <Route path="/admin/notifications" element={<NotificationManagementPage />} />
              <Route path="/admin/email-campaigns" element={<EmailCampaignPage />} />
              <Route path="/admin/templates" element={<TemplateImportPage />} />
              <Route path="/admin/credits" element={<CreditManagementPage />} />
              <Route path="/admin/payment-settings" element={<PaymentSettingsPage />} />
              <Route path="/admin/beta" element={<BetaDashboardPage />} />
              <Route path="/admin/jurisprudence/import" element={<JurisprudenceImportPage />} />
              <Route path="/admin/jurisprudence/list" element={<JurisprudenceListPage />} />
              <Route path="/admin/ai-settings" element={<AISettingsPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <style dangerouslySetInnerHTML={{ __html: globalStyles }} />
        
        <Router>
          <AuthProvider>
            <CreditProvider>
              <NotificationProvider>
                <AppRoutes />
              </NotificationProvider>
            </CreditProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
      {/* DevTools apenas em desenvolvimento */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App; 